import { useCallback, useRef, useState } from "react";
import { fieldCommitOnEnterKeyDown } from "../utils/fieldCommitOnEnterKeyDown";

/**
 * True when blur should commit a multi-control field group.
 * - No event / no relatedTarget → commit (programmatic calls, e.g. TimeSelect after pick).
 * - Focus still inside `container` → do not commit.
 *
 * @param {FocusEvent|undefined|null} e
 * @param {Element|null|undefined} container
 */
export function shouldCommitGroupedFieldBlur(e, container) {
  if (!e || typeof e !== "object" || !("relatedTarget" in e)) return true;
  const related = e.relatedTarget;
  if (!(container instanceof Node) || !(related instanceof Node)) return true;
  return !container.contains(related);
}

/**
 * onBlur for a wrapper around multiple controls (date + time, etc.).
 * @param {{ current: Element|null }|null|undefined} containerRef
 * @param {() => void} onCommit
 */
export function createGroupedBlurHandler(containerRef, onCommit) {
  return (e) => {
    if (!shouldCommitGroupedFieldBlur(e, containerRef?.current)) return;
    if (typeof onCommit === "function") onCommit();
  };
}

function waitMacrotask() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

/**
 * Blur-driven autosave hook. Enter-to-commit is {@link fieldCommitOnEnterKeyDown} with the same `onBlur` handlers.
 * On textareas, **Ctrl+Enter** / **Cmd+Enter** inserts a newline; plain **Enter** commits (blur).
 *
 * @param {object} options
 * @param {boolean} options.hasChanges
 * @param {() => Promise<void>} options.saveFn
 * @param {() => void} [options.onExitEdit] Called after blur commit (e.g. return inline field to read-only).
 */
function useBlurAutoSave({ hasChanges, saveFn, onExitEdit }) {
  const inFlightRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const hasChangesRef = useRef(hasChanges);
  const saveFnRef = useRef(saveFn);
  const onExitEditRef = useRef(onExitEdit);

  // Keep refs current during render so deferred picker commits (SearchableSelect / TimeSelect /
  // RepeatPatternPicker) see fresh hasChanges before useEffect would run.
  hasChangesRef.current = hasChanges;
  saveFnRef.current = saveFn;
  onExitEditRef.current = onExitEdit;

  const saveIfNeeded = useCallback(async () => {
    if (!hasChangesRef.current || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsSaving(true);
    try {
      await saveFnRef.current();
    } finally {
      setIsSaving(false);
      inFlightRef.current = false;
    }
  }, []);

  const handleFieldBlur = useCallback(() => {
    void (async () => {
      // Pickers often call onChange then onBlur in the same turn (or one setTimeout later).
      // Wait for React to commit parent state, then retry once if still dirty.
      await waitMacrotask();
      await saveIfNeeded();
      if (hasChangesRef.current) {
        await waitMacrotask();
        await saveIfNeeded();
      }
      onExitEditRef.current?.();
    })();
  }, [saveIfNeeded]);

  /** Blur handler for multi-control groups; only commits when focus leaves `containerRef`. */
  const createGroupedFieldBlurHandler = useCallback(
    (containerRef) => createGroupedBlurHandler(containerRef, handleFieldBlur),
    [handleFieldBlur]
  );

  const handleFieldCommitKeyDown = useCallback((e) => {
    fieldCommitOnEnterKeyDown(e);
  }, []);

  return {
    isSaving,
    handleFieldBlur,
    createGroupedFieldBlurHandler,
    handleFieldCommitKeyDown,
    saveIfNeeded,
  };
}

export default useBlurAutoSave;
